import { Axios } from '@utils/api/axios-instance';
import { BaseError, BaseResponse } from '@utils/types/shared/base';
import { AxiosError } from 'axios';
import { POST_URL } from '@utils/api/endpoints';
import { useMutation, useQueryClient } from 'react-query';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { CUSTOM_MODEL_QUERY_KEYS } from './query-keys';

export interface CreateCustomModelPayload {
  name: string;
  language_id: number;
  organization_id: number;
}

export interface CreateCustomModelResponse {
  result:
    | { id: number; name: string; type: number }
    | Array<{ id: number; name: string; type: number }>;
  count?: number;
  total_results?: number;
}

async function postCustomModel(
  payload: CreateCustomModelPayload
): Promise<BaseResponse<CreateCustomModelResponse>> {
  const url = `${POST_URL().customModel}`;
  const { data } = await Axios.post(url, payload);
  return data;
}

export function useCreateCustomModel() {
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  return useMutation<
    BaseResponse<CreateCustomModelResponse>,
    AxiosError<BaseError>,
    CreateCustomModelPayload
  >(postCustomModel, {
    onSuccess: () => {
      // Invalidate all custom model queries to refresh the list
      queryClient.invalidateQueries(CUSTOM_MODEL_QUERY_KEYS.all);
      toast.success(t('custom-model-created-successfully'));
    },
    onError: error => {
      const fallbackMessage = t(
        'an-error-occurred-while-creating-custom-model'
      );
      const data = error?.response?.data;
      const messages = Array.isArray(data?.detail)
        ? data.detail.map(
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            (err: any) => `${err.loc?.[1] ?? 'field'}: ${err.msg}`
          )
        : [data?.message || data?.error_description || fallbackMessage];
      toast.error(messages.join('\n'));
    },
    useErrorBoundary: false
  });
}
